import { useRef } from "react";
import { FiChevronRight, FiLogOut } from "react-icons/fi";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { BooleanParam, StringParam, useQueryParams } from "use-query-params";

import DropdownOld from "../components/common/DropdownOld";
import FlexBox from "../components/common/FlexBox";
import { Body2, ButtonText, H4, H6 } from "../components/common/Headings";
import useMobileView from "../hooks/useMobileView";
import useOutsideAlert from "../hooks/useOutsideAlert";
import { validIdGenerator } from "../utils/helper";
import { CF_PROVIDER_ROLE } from "../variable";

const DropdownContainer = styled(FlexBox)`
  right: 0;
  top: 2.5rem;
  width: 22.5rem;
  position: absolute;
`;

const DropdownInterior = styled(FlexBox)`
  flex-direction: column;
`;

const ProfileDropdownInterior = styled(DropdownInterior)`
  overflow: hidden;
  border-radius: 1rem;
  max-height: calc(100vh - 4rem);
`;

const ScrollableOptions = styled(FlexBox)`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  flex-direction: column;
`;

const DropdownOption = styled(FlexBox)`
  padding: 1rem;
  cursor: pointer;
  column-gap: 0.5rem;
  align-items: center;
  justify-content: space-between;
  transition: background-color 250ms ease-in-out;

  svg {
    color: var(--accent-500);
    transition: color 250ms ease-in-out;
  }

  :hover {
    background-color: var(--accent-200);

    svg {
      color: var(--accent-800);
    }
  }
`;

const ProfileImage = styled.img`
  width: 3rem;
  height: 4.5rem;
  overflow: hidden;
  object-fit: cover;
  border-radius: 0.5rem;
`;

export const ProfileDropdown = ({
  user,
  userName,
  supervisionMode,
  toggleDropdown = () => {},
  toggleLogoutModal,
}) => {
  const containerRef = useRef(null);
  useOutsideAlert(containerRef, toggleDropdown, "header-user-image");

  const profileOptions = [
    { label: "Availability", link: "/availability" },
    {
      label: "Personal Information",
      link: "/profile?currentOption=personalinformation",
    },
    {
      label: "About & FAQs",
      link: "/profile?currentOption=about",
    },
    {
      label: "Qualifications & Experience",
      link: "/profile?currentOption=qualifications",
    },
    { label: "Files & Documents", link: "/profile?currentOption=documents" },
    { label: "Fees & Payments", link: "/profile?currentOption=feeandpayment" },
    {
      label: "Advanced Information",
      link: "/profile?currentOption=advanceoptions",
    },
  ];

  const navigate = useNavigate();

  return (
    <DropdownContainer ref={containerRef}>
      <DropdownOld>
        <ProfileDropdownInterior>
          <FlexBox align="center" columnGap="1rem" padding="1.5rem" flex="none">
            <ProfileImage
              alt="User Image"
              draggable="false"
              src={"https:" + user?.image}
            />

            <FlexBox column rowGap="0.375rem">
              <H4 bold>{userName}</H4>
              {user?.doj && (
                <H6 color="var(--accent-700)">joined on {user?.doj}</H6>
              )}
            </FlexBox>
          </FlexBox>

          <ScrollableOptions className="custom-scrollbar">
            {profileOptions?.map(({ label, link }) => (
              <DropdownOption
                id={`${validIdGenerator(label)}-option`}
                key={label}
                onClick={() => navigate(link)}
              >
                <Body2 bold fontSize="0.875rem" color="var(--accent-800)">
                  {label}
                </Body2>
                <FiChevronRight size="1.5rem" />
              </DropdownOption>
            ))}

            {!supervisionMode && (
              <FlexBox
                justify="flex-end"
                padding="1.5rem"
                onClick={toggleLogoutModal}
              >
                <FlexBox
                  id="profile-logout"
                  align="center"
                  cursor="pointer"
                  columnGap="0.5rem"
                >
                  <FiLogOut
                    size="1.125rem"
                    strokeWidth={2.75}
                    color="var(--primary-800)"
                  />
                  <ButtonText color="var(--primary-800)">LOG OUT</ButtonText>
                </FlexBox>
              </FlexBox>
            )}
          </ScrollableOptions>
        </ProfileDropdownInterior>
      </DropdownOld>
    </DropdownContainer>
  );
};

export const ActionsDropdown = ({ toggleDropdown = () => {} }) => {
  const containerRef = useRef(null);
  useOutsideAlert(containerRef, toggleDropdown, "header-actions");
  const isMobile = useMobileView();

  const providerProfile = useSelector(state => state.provider.providerProfile);
  const isCfProvider = providerProfile?.roles?.some(role =>
    CF_PROVIDER_ROLE.includes(role)
  );
  const isAIEnabled = useSelector(state => state.featureFlags.ai_enabled);

  const navigate = useNavigate();
  const [, setQueryParams] = useQueryParams({
    showLinkedAccountsModal: BooleanParam,
    showAIConsentModal: BooleanParam,
    aiConsentSource: StringParam,
  });

  const actionOptions = [
    { id: "amaha-ai", label: "Amaha AI" },
    {
      id: "provider-tagging",
      label: isCfProvider ? "Service Tagging" : "Provider Tagging",
      link: "/provider-tagging/update-history",
    },
    { id: "transactions", label: "Transactions", link: "/transactions" },
    {
      id: "canned-messages",
      label: "Canned Messages",
      link: "/canned-messages",
    },
    {
      id: "help-and-updates",
      label: "Help & Updates",
      link: "/help-and-updates",
    },
    { id: "linked-accounts", label: "Linked Accounts" },
  ];

  let visibleOptions = actionOptions;

  if (isCfProvider) {
    visibleOptions = visibleOptions.filter(
      option => !["canned-messages", "amaha-ai"].includes(option.id)
    );
  }

  if (!isAIEnabled) {
    visibleOptions = visibleOptions.filter(option => option.id !== "amaha-ai");
  }

  if (isMobile) {
    visibleOptions = visibleOptions.filter(option => option.id !== "amaha-ai");
  }

  const handleOptionClick = (id, link) => {
    if (id === "amaha-ai") {
      navigate("/ai-home");
    } else if (id === "linked-accounts") {
      setQueryParams({ showLinkedAccountsModal: 1 }, "replaceIn");
    } else navigate(link);
    toggleDropdown();
  };

  return (
    <DropdownContainer ref={containerRef}>
      <DropdownOld>
        <DropdownInterior padding="1.5rem 0">
          {visibleOptions?.map(({ label, link, id }) => (
            <DropdownOption
              key={label}
              onClick={() => handleOptionClick(id, link)}
            >
              <Body2 bold fontSize="0.875rem" color="var(--accent-800)">
                {label}
              </Body2>
              <FiChevronRight size="1.5rem" />
            </DropdownOption>
          ))}
        </DropdownInterior>
      </DropdownOld>
    </DropdownContainer>
  );
};

export default {
  ProfileDropdown,
  ActionsDropdown,
};
